import { useRef, useState, useEffect } from "react";
import { X, ArrowRight, ArrowLeft, Mic, Square, Paperclip, Loader2, Check, Zap } from "lucide-react";
import { BrutalButton } from "@/components/Brutal";
import { DatePicker } from "@/components/DatePicker";
import { GroupMultiSelect, PersonMultiSelect } from "@/components/Selects";
import { AiRewriteButton } from "@/components/AiComponents";
import { useAuth, isAdmin } from "@/context/AuthContext";
import { DUE_OPTIONS, RECURRENCE, WEEKDAYS } from "@/lib/labels";
import { useT } from "@/lib/i18n";
import api from "@/lib/api";
import { toast } from "sonner";

const TYPES = ["algemeen", "schoonmaak", "koken", "boodschappen", "afval", "onderhoud", "administratie"];
const empty = {
  title: "", description: "", task_type: "algemeen", group_ids: [], assignee_ids: [],
  due_when: "vandaag", due_time: "", due_date: new Date().toISOString().slice(0, 10), one_time: true, recurrence: null, recurrence_weekday: null, recurrence_weekdays: [],
  transferable: true, require_confirmation: false, is_quick_tile: false, attachments: [], auto_assign: false, assign_mode: "shared",
};

export default function TaskWizard({ groups, users, onClose, onCreated, task }) {
  const tr = useT();
  const { user } = useAuth();
  const admin = isAdmin(user);
  const [applyScope, setApplyScope] = useState("one");
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(task ? {
    title: task.title || "", description: task.description || "", task_type: task.task_type || "algemeen",
    group_ids: task.group_ids || [], assignee_ids: task.assignee_ids || (task.assignee_id ? [task.assignee_id] : []),
    due_when: task.due_when || "vandaag", due_time: task.due_time || "", due_date: task.next_date || new Date().toISOString().slice(0, 10), one_time: task.one_time !== false,
    recurrence: task.recurrence || null, recurrence_weekday: task.recurrence_weekday ?? null,
    transferable: task.transferable !== false, require_confirmation: !!task.require_confirmation,
    is_quick_tile: !!task.is_quick_tile, attachments: task.attachments || [], auto_assign: !!task.auto_assign,
    assign_mode: task.assign_mode || "shared",
    image_url: task.image_url || null,
  } : empty);
  const [busy, setBusy] = useState(false);
  const [autoAssignEnabled, setAutoAssignEnabled] = useState(false);
  useEffect(() => { api.get("/settings").then((r) => setAutoAssignEnabled(!!r.data.auto_assign_past_due)).catch(() => {}); }, []);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const isTile = form.is_quick_tile;
  const steps = isTile ? ["Wat", "Opties"] : ["Wat", "Wanneer", "Wie & Opties"];
  const stepName = steps[step];

  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => chunksRef.current.push(e.data);
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        setTranscribing(true);
        try {
          const fd = new FormData();
          fd.append("file", blob, "opname.webm");
          const { data } = await api.post("/transcribe", fd);
          set("description", (form.description ? form.description + " " : "") + data.text);
          toast.success(tr("Tekst toegevoegd"));
        } catch { toast.error(tr("Transcriptie mislukt")); }
        finally { setTranscribing(false); }
      };
      rec.start();
      recorderRef.current = rec;
      setRecording(true);
    } catch { toast.error(tr("Geen microfoon toegang")); }
  };
  const stopRec = () => { recorderRef.current?.stop(); setRecording(false); };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd);
      set("attachments", [...form.attachments, { url: data.url, filename: data.filename, content_type: data.content_type }]);
      toast.success(tr("Bijlage toegevoegd"));
    } catch { toast.error(tr("Upload mislukt")); }
    finally { setUploading(false); }
  };

  const create = async () => {
    setBusy(true);
    try {
      const seriesEdit = task && !form.one_time && form.recurrence && applyScope === "series";
      if (task) await api.put(`/tasks/${task.id}${seriesEdit ? "?scope=series" : ""}`, form);
      else await api.post("/tasks", form);
      toast.success(task ? (seriesEdit ? "Hele reeks bijgewerkt ✅" : "Opgeslagen") : isTile ? "Quick Tile aangemaakt! ⚡" : "Taak aangemaakt! 🎉");
      onCreated();
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Mislukt"); }
    finally { setBusy(false); }
  };

  const lastStep = step === steps.length - 1;

  return (
    <div className="fixed inset-0 z-50 bg-brand-ink/40 flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="task-wizard">
      <div className="bg-brand-cream w-full max-w-lg rounded-t-3xl sm:rounded-3xl brutal max-h-[92vh] overflow-auto no-scrollbar animate-pop">
        <div className="sticky top-0 bg-brand-cream border-b-2 border-brand-ink px-5 py-4 flex items-center justify-between">
          <h2 className="font-head font-black text-xl">{task ? (isTile ? tr("Quick Tile bewerken") : tr("Taak bewerken")) : (isTile ? tr("Nieuwe Quick Tile") : tr("Nieuwe taak"))}</h2>
          <button data-testid="wizard-close" onClick={onClose} className="brutal-sm brutal-press rounded-full p-1.5 bg-white"><X className="w-4 h-4" strokeWidth={3} /></button>
        </div>

        <div className="px-5 pt-4">
          <div className="flex gap-2 mb-1">
            {steps.map((s, i) => (
              <div key={s} className={`h-2.5 flex-1 rounded-full border-2 border-brand-ink ${i <= step ? "bg-brand-violet" : "bg-white"}`} />
            ))}
          </div>
          <p className="text-xs font-bold text-brand-ink/50 mb-4">{tr("Stap")} {step + 1} {tr("van")} {steps.length} — {tr(stepName)}</p>
        </div>

        <div className="px-5 pb-6 space-y-4">
          {stepName === "Wat" && (
            <>
              {!task && admin && (
                <button data-testid="toggle-is_quick_tile" onClick={() => set("is_quick_tile", !isTile)}
                  className={`w-full brutal-sm rounded-xl px-4 py-3 flex items-center justify-between font-bold text-sm ${isTile ? "bg-brand-violet text-white" : "bg-white"}`}>
                  <span className="flex items-center gap-2"><Zap className="w-4 h-4" strokeWidth={2.5} />Dit is een Quick Tile (sjabloon)</span>
                  <span className={`w-6 h-6 rounded-md border-2 flex items-center justify-center ${isTile ? "bg-white border-white" : "border-brand-ink bg-white"}`}>
                    {isTile && <Check className="w-4 h-4 text-brand-violet" strokeWidth={3} />}
                  </span>
                </button>
              )}
              {isTile && <p className="text-xs font-semibold text-brand-ink/60 -mt-1">{tr("Bij een Quick Tile leg je alleen het \"wat\" vast. Wanneer & voor wie kies je pas bij gebruik.")}</p>}
              <div>
                <label className="font-head font-bold text-sm">{tr("Titel")}</label>
                <input data-testid="task-title-input" value={form.title} onChange={(e) => set("title", e.target.value)} autoFocus
                  placeholder={tr("Bijv. Afwas doen")} className="w-full brutal-sm rounded-xl px-4 py-3 font-medium outline-none mt-1" />
              </div>
              <div>
                <label className="font-head font-bold text-sm">{tr("Type")}</label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {TYPES.map((t) => (
                    <button key={t} data-testid={`type-${t}`} onClick={() => set("task_type", t)}
                      className={`brutal-sm brutal-press rounded-full px-3 py-1.5 text-sm font-bold capitalize ${form.task_type === t ? "bg-brand-blue text-white" : "bg-white"}`}>{tr(t)}</button>
                  ))}
                </div>
              </div>
              <div>
                <label className="font-head font-bold text-sm flex items-center justify-between">
                  {tr("Omschrijving")}
                  <span className="flex items-center gap-2">
                    <AiRewriteButton value={form.description} onChange={(v) => set("description", v)} context={form.title} testid="task-ai-rewrite" />
                    <button data-testid="mic-btn" onClick={recording ? stopRec : startRec} disabled={transcribing}
                      className={`brutal-sm brutal-press rounded-full p-2 ${recording ? "bg-brand-pink" : "bg-white"}`}>
                      {transcribing ? <Loader2 className="w-4 h-4 animate-spin" /> : recording ? <Square className="w-4 h-4" strokeWidth={3} /> : <Mic className="w-4 h-4" strokeWidth={2.5} />}
                    </button>
                    <label data-testid="attach-btn" className="brutal-sm brutal-press rounded-full p-2 bg-white cursor-pointer">
                      {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" strokeWidth={2.5} />}
                      <input type="file" className="hidden" onChange={upload} accept="image/*,video/*,.pdf,.doc,.docx" />
                    </label>
                  </span>
                </label>
                <textarea data-testid="task-desc-input" value={form.description} onChange={(e) => set("description", e.target.value)} rows={3}
                  placeholder={tr("Vaste omschrijving of spreek in met de microfoon…")} className="w-full brutal-sm rounded-xl px-4 py-3 font-medium outline-none mt-1" />
                {form.attachments.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {form.attachments.map((a, i) => (
                      <span key={i} className="brutal-sm rounded-full px-2 py-1 text-xs font-bold bg-white flex items-center gap-1">
                        <Paperclip className="w-3 h-3" strokeWidth={3} />{a.filename?.slice(0, 16)}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}

          {stepName === "Wanneer" && (
            <>
              <div>
                <label className="font-head font-bold text-sm">{tr("Wanneer moet het klaar zijn?")}</label>
                <div className="grid grid-cols-3 gap-2 mt-1">
                  {DUE_OPTIONS.map((d) => (
                    <button key={d.value} data-testid={`due-${d.value}`} onClick={() => set("due_when", d.value)}
                      className={`brutal-sm brutal-press rounded-xl py-3 flex flex-col items-center gap-1 font-bold text-sm ${form.due_when === d.value ? d.color + " " + (d.text || "text-brand-ink") : "bg-white"}`}>
                      <d.icon className="w-5 h-5" strokeWidth={2.5} />{tr(d.label)}
                    </button>
                  ))}
                </div>
                {!form.one_time && form.due_when === "specifiek" && (
                  <input data-testid="due-time-input" type="time" value={form.due_time} onChange={(e) => set("due_time", e.target.value)}
                    className="w-full brutal-sm rounded-xl px-4 py-3 font-medium outline-none mt-2" />
                )}
                {form.one_time && form.due_when !== "nu" && (
                  <div className={`mt-2 grid gap-2 ${form.due_when === "specifiek" ? "grid-cols-2" : "grid-cols-1"}`}>
                    <div>
                      <label className="text-xs font-bold text-brand-ink/60">{tr("Datum")}</label>
                      <DatePicker testid="due-date-input" value={form.due_date} onChange={(v) => set("due_date", v)} className="mt-1" />
                    </div>
                    {form.due_when === "specifiek" && (
                      <div>
                        <label className="text-xs font-bold text-brand-ink/60">{tr("Tijd")}</label>
                        <input data-testid="due-time-input" type="time" value={form.due_time} onChange={(e) => set("due_time", e.target.value)}
                          className="w-full brutal-sm rounded-xl px-4 py-3 font-medium outline-none mt-1" />
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div>
                <label className="font-head font-bold text-sm">{tr("Herhaling")}</label>
                <div className="flex gap-2 mt-1">
                  <button data-testid="one-time-btn" onClick={() => { set("one_time", true); set("recurrence", null); }}
                    className={`brutal-sm brutal-press rounded-full px-4 py-2 font-bold text-sm ${form.one_time ? "bg-brand-green" : "bg-white"}`}>{tr("Eenmalig")}</button>
                  <button data-testid="repeat-btn" onClick={() => {
                      if (!admin) { toast.error(tr("Alleen een beheerder kan een herhaaltaak aanmaken")); return; }
                      set("one_time", false); set("recurrence", "wekelijks");
                    }}
                    className={`brutal-sm brutal-press rounded-full px-4 py-2 font-bold text-sm ${!form.one_time ? "bg-brand-orange text-white" : "bg-white"} ${!admin ? "opacity-60" : ""}`}
                    title={!admin ? tr("Alleen een beheerder kan een herhaaltaak aanmaken") : undefined}>{tr("Herhalen")}{!admin && " 🔒"}</button>
                </div>
                {!form.one_time && (
                  <>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {RECURRENCE.map((r) => (
                        <button key={r.value} data-testid={`rec-${r.value}`} onClick={() => set("recurrence", r.value)}
                          className={`brutal-sm brutal-press rounded-full px-3 py-1.5 text-sm font-bold ${form.recurrence === r.value ? "bg-brand-blue text-white" : "bg-white"}`}>{tr(r.label)}</button>
                      ))}
                    </div>
                    {(form.recurrence === "wekelijks" || form.recurrence === "twee_wekelijks") && (
                      <>
                        <p className="text-xs font-bold text-brand-ink/50 mt-2">{tr("Op welke dag(en)? Meerdere mogelijk.")}</p>
                        <div className="flex flex-wrap gap-1.5 mt-1">
                          {WEEKDAYS.map((d, i) => {
                            const wds = form.recurrence_weekdays || [];
                            const on = wds.includes(i);
                            return (
                              <button key={d} type="button" data-testid={`rec-weekday-${i}`}
                                onClick={() => set("recurrence_weekdays", on ? wds.filter((x) => x !== i) : [...wds, i])}
                                className={`brutal-sm brutal-press rounded-full px-2.5 py-1 text-xs font-bold ${on ? "bg-brand-pink" : "bg-white"}`}>{d.slice(0, 2)}</button>
                            );
                          })}
                        </div>
                        {form.recurrence === "twee_wekelijks" && (form.recurrence_weekdays || []).length > 0 &&
                          <p className="text-xs text-brand-ink/50 mt-1">{tr("Elke 2 weken op de gekozen dag(en).")}</p>}
                      </>
                    )}
                  </>
                )}
              </div>
            </>
          )}

          {(stepName === "Wie & Opties" || stepName === "Opties") && (
            <>
              <div>
                <label className="font-head font-bold text-sm">Groepen</label>
                <div className="mt-1">
                  <GroupMultiSelect groups={groups} value={form.group_ids} onChange={(v) => set("group_ids", v)} testid="wiz-group" />
                </div>
              </div>
              {(stepName === "Wie & Opties" || stepName === "Opties") && (
                <div>
                  <label className="font-head font-bold text-sm">{tr("Toewijzen aan")}</label>
                  {isTile && <p className="text-xs font-semibold text-brand-ink/50 -mt-0.5 mb-1">{tr("Optioneel — vast gekoppelde personen worden bij gebruik automatisch toegewezen.")}</p>}
                  <div className="mt-1">
                    <PersonMultiSelect users={users} value={form.assignee_ids} onChange={(v) => set("assignee_ids", v)} testid="wiz-assignee" placeholder={tr("Zoek huisgenoot… (leeg = vrij op te pakken)")} />
                  </div>
                  {groups?.length > 0 && (
                    <div className="mt-2">
                      <p className="text-xs font-bold text-brand-ink/50 mb-1">{tr("Of wijs een hele groep toe:")}</p>
                      <div className="flex flex-wrap gap-1.5">
                        {groups.map((g) => {
                          const members = users.filter((u) => (u.group_ids || []).includes(g.id)).map((u) => u.user_id);
                          const allIn = members.length > 0 && members.every((id) => form.assignee_ids.includes(id));
                          return (
                            <button key={g.id} type="button" data-testid={`wiz-assign-group-${g.id}`}
                              onClick={() => {
                                if (members.length === 0) { toast.info(tr("Deze groep heeft geen leden")); return; }
                                set("assignee_ids", allIn
                                  ? form.assignee_ids.filter((id) => !members.includes(id))
                                  : Array.from(new Set([...form.assignee_ids, ...members])));
                              }}
                              className={`brutal-sm brutal-press rounded-full px-3 py-1.5 text-xs font-bold flex items-center gap-1 ${allIn ? "bg-brand-violet text-white" : "bg-white"}`}>
                              {g.name}{allIn && <Check className="w-3 h-3" strokeWidth={3} />}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {form.assignee_ids.length >= 2 && (
                    <div className="mt-3 rounded-xl border-2 border-brand-ink bg-brand-cream p-3" data-testid="assign-mode-choice">
                      <p className="text-xs font-black font-head mb-2">{isTile ? tr("Als deze tegel voor meerdere mensen is:") : tr("Deze taak is voor meerdere mensen — hoe verdelen?")}</p>
                      <div className="space-y-2">
                        {[
                          { v: "shared", title: tr("Eén gedeelde taak"), sub: tr("Wie 'm oppakt, is 'ie klaar voor de hele groep.") },
                          { v: "per_person", title: tr("Aparte taak per persoon"), sub: tr("Iedereen krijgt een eigen taak en vinkt zelf af.") },
                        ].map((o) => {
                          const active = (form.assign_mode || "shared") === o.v;
                          return (
                            <button key={o.v} type="button" data-testid={`assign-mode-${o.v}`} onClick={() => set("assign_mode", o.v)}
                              className={`w-full text-left brutal-sm rounded-xl px-3 py-2.5 flex items-start gap-2 transition-colors ${active ? "bg-brand-violet text-white" : "bg-white"}`}>
                              <span className={`w-5 h-5 mt-0.5 rounded-full border-2 shrink-0 flex items-center justify-center ${active ? "border-white bg-white" : "border-brand-ink"}`}>
                                {active && <span className="w-2.5 h-2.5 rounded-full bg-brand-violet" />}
                              </span>
                              <span>
                                <span className="block font-bold text-sm">{o.title}</span>
                                <span className={`block text-xs ${active ? "text-white/80" : "text-brand-ink/60"}`}>{o.sub}</span>
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}
              <div className="space-y-2">
                {[
                  { k: "transferable", label: "Mag worden overgedragen" },
                  { k: "require_confirmation", label: "Geef mij een seintje zodra afgevinkt" },
                ].map((o) => (
                  <button key={o.k} data-testid={`toggle-${o.k}`} onClick={() => set(o.k, !form[o.k])}
                    className="w-full brutal-sm rounded-xl px-4 py-3 bg-white flex items-center justify-between font-bold text-sm">
                    {tr(o.label)}
                    <span className={`w-6 h-6 rounded-md border-2 border-brand-ink flex items-center justify-center ${form[o.k] ? "bg-brand-green" : "bg-white"}`}>
                      {form[o.k] && <Check className="w-4 h-4" strokeWidth={3} />}
                    </span>
                  </button>
                ))}
              </div>
              {autoAssignEnabled && stepName === "Wie & Opties" && (
                <button data-testid="toggle-auto_assign" onClick={() => set("auto_assign", !form.auto_assign)}
                  className="w-full brutal-sm rounded-xl px-4 py-3 bg-white flex items-center justify-between font-bold text-sm">
                  <span>{tr("Automatisch toewijzen bij verlopen")}<span className="block text-xs text-brand-ink/50 font-semibold">{tr("Slim & eerlijk verdeeld")}</span></span>
                  <span className={`w-6 h-6 rounded-md border-2 border-brand-ink flex items-center justify-center shrink-0 ${form.auto_assign ? "bg-brand-green" : "bg-white"}`}>
                    {form.auto_assign && <Check className="w-4 h-4" strokeWidth={3} />}
                  </span>
                </button>
              )}
            </>
          )}
        </div>

        {task && !form.one_time && form.recurrence && lastStep && (
          <div className="px-5 pb-1 pt-1">
            <p className="text-xs font-bold text-brand-ink/50 mb-1.5">{tr("Wijziging toepassen op")}</p>
            <div className="flex gap-2">
              <button data-testid="edit-scope-one" onClick={() => setApplyScope("one")}
                className={`flex-1 brutal-sm brutal-press rounded-xl px-3 py-2 font-bold text-sm ${applyScope === "one" ? "bg-brand-violet text-white" : "bg-white"}`}>{tr("Alleen deze keer")}</button>
              <button data-testid="edit-scope-series" onClick={() => setApplyScope("series")}
                className={`flex-1 brutal-sm brutal-press rounded-xl px-3 py-2 font-bold text-sm ${applyScope === "series" ? "bg-brand-violet text-white" : "bg-white"}`}>{tr("Hele reeks")}</button>
            </div>
          </div>
        )}
        <div className="sticky bottom-0 bg-brand-cream border-t-2 border-brand-ink px-5 py-4 flex items-center justify-between gap-3">
          {step > 0 ? (
            <BrutalButton variant="white" onClick={() => setStep(step - 1)} data-testid="wizard-back">
              <ArrowLeft className="w-4 h-4" strokeWidth={3} /> {tr("Terug")}
            </BrutalButton>
          ) : <span />}
          {!lastStep ? (
            <BrutalButton onClick={() => setStep(step + 1)} disabled={step === 0 && !form.title} data-testid="wizard-next">
              {tr("Volgende")} <ArrowRight className="w-4 h-4" strokeWidth={3} />
            </BrutalButton>
          ) : (
            <BrutalButton variant="green" onClick={create} disabled={busy} data-testid="wizard-create">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" strokeWidth={3} />} {task ? tr("Opslaan") : isTile ? tr("Quick Tile aanmaken") : tr("Taak aanmaken")}
            </BrutalButton>
          )}
        </div>
      </div>
    </div>
  );
}
